// Phase 5 (web revival) — typed fetch wrapper.
//
// Three jobs:
// 1. Attach the access token to every authed request as `Authorization:
//    Bearer …`.
// 2. On a 401 with `code: auth.expired_token` or `auth.invalid_token`,
//    transparently POST /v1/auth/refresh (cookie-only) and replay the
//    original request once. If the refresh itself 401s, clear the
//    session — the router's auth gate will redirect to /login.
// 3. Normalize JSON error envelopes into thrown `ApiError`s with the
//    server's `code` + `reason` preserved.
//
// All requests use `credentials: 'include'` so the HttpOnly refresh
// cookie rides along on /v1/auth/refresh. Same-origin in production
// (FileMiddleware + API on :8080); dev mode proxies through Vite so the
// browser still considers it same-origin.

import {
  clearSession,
  getAccessToken,
  isAccessTokenFresh,
  setSession,
} from '../app/auth';
import type { ApiErrorBody, AuthEnvelope } from './types';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly reason: string,
    public readonly field?: string,
    public readonly retryAfterSeconds?: number,
  ) {
    super(`[${status} ${code}] ${reason}${field ? ` (field=${field})` : ''}`);
    this.name = 'ApiError';
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';

interface ApiRequestOptions {
  method?: Method;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /// Skip the Authorization header. Used by /v1/auth/password (no token
  /// yet) and /v1/auth/refresh (cookie is the credential).
  unauthenticated?: boolean;
  /// Skip the 401-refresh-replay loop. Used internally by `refreshAccessToken`
  /// to avoid infinite recursion.
  noRefresh?: boolean;
  /// Override Content-Type. Default for non-null bodies is
  /// application/json; flip to e.g. 'image/*' when uploading raw bytes.
  contentType?: string;
  /// Pass a non-default Accept (e.g. 'image/jpeg' for preview blobs).
  accept?: string;
  signal?: AbortSignal;
}

const REFRESH_PATH = '/v1/auth/refresh';

let refreshInFlight: Promise<void> | null = null;

async function refreshAccessToken(): Promise<void> {
  // De-dupe concurrent 401s. Without this, a library full of in-flight
  // grid thumbnails would each trigger their own refresh on a stale
  // token boot.
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const env = await apiRequest<AuthEnvelope>(REFRESH_PATH, {
        method: 'POST',
        body: {},
        unauthenticated: true,
        noRefresh: true,
      });
      setSession(env);
    } catch (err) {
      clearSession();
      throw err;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function buildURL(path: string, query?: ApiRequestOptions['query']): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

async function rawRequest<T>(
  path: string,
  opts: ApiRequestOptions,
): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {};
  if (!opts.unauthenticated) {
    const token = getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  if (opts.accept) headers['Accept'] = opts.accept;

  let body: BodyInit | undefined;
  if (opts.body !== undefined && method !== 'GET' && method !== 'HEAD') {
    if (opts.contentType) {
      headers['Content-Type'] = opts.contentType;
      body = opts.body as BodyInit;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
  }

  const url = buildURL(path, opts.query);
  const resp = await fetch(url, {
    method,
    headers,
    body,
    credentials: 'include',
    signal: opts.signal,
  });

  if (resp.status === 204) {
    return undefined as T;
  }

  const ctype = resp.headers.get('content-type') ?? '';

  if (!resp.ok) {
    let errBody: ApiErrorBody | null = null;
    if (ctype.includes('application/json')) {
      try {
        errBody = (await resp.json()) as ApiErrorBody;
      } catch {
        /* ignore */
      }
    }
    const retryAfter = resp.headers.get('retry-after');
    throw new ApiError(
      resp.status,
      errBody?.code ?? `http.${resp.status}`,
      errBody?.reason ?? resp.statusText,
      errBody?.field,
      errBody?.retry_after_seconds ?? (retryAfter ? Number(retryAfter) : undefined),
    );
  }

  if (ctype.includes('application/json')) {
    return (await resp.json()) as T;
  }
  // Caller asked for an image/blob/text payload via `accept` — hand back
  // the raw Response. Editor preview is the main caller; it wants the
  // blob URL, not parsed JSON.
  return resp as unknown as T;
}

export async function apiRequest<T>(
  path: string,
  opts: ApiRequestOptions = {},
): Promise<T> {
  // Cheap pre-flight: if we know the access token is past its TTL, do
  // the refresh BEFORE the request so we don't waste a round-trip on a
  // guaranteed 401. Skip when the caller explicitly opted out (refresh
  // itself, login, etc.).
  if (
    !opts.unauthenticated &&
    !opts.noRefresh &&
    getAccessToken() !== null &&
    !isAccessTokenFresh()
  ) {
    try {
      await refreshAccessToken();
    } catch {
      // Refresh failed — fall through, the real request will 401 and
      // the catch below will redirect via clearSession.
    }
  }

  try {
    return await rawRequest<T>(path, opts);
  } catch (err) {
    if (
      err instanceof ApiError &&
      err.status === 401 &&
      !opts.noRefresh &&
      !opts.unauthenticated &&
      (err.code === 'auth.expired_token' ||
        err.code === 'auth.invalid_token')
    ) {
      try {
        await refreshAccessToken();
      } catch {
        throw err;
      }
      return await rawRequest<T>(path, opts);
    }
    throw err;
  }
}

// ───────────────────────────────────────────────────────────────────────
// Convenience helpers — keeps call sites tidy.
// ───────────────────────────────────────────────────────────────────────

export const api = {
  get<T>(path: string, query?: ApiRequestOptions['query']) {
    return apiRequest<T>(path, { method: 'GET', query });
  },
  post<T>(path: string, body?: unknown, query?: ApiRequestOptions['query']) {
    return apiRequest<T>(path, { method: 'POST', body, query });
  },
  put<T>(path: string, body?: unknown) {
    return apiRequest<T>(path, { method: 'PUT', body });
  },
  patch<T>(path: string, body?: unknown) {
    return apiRequest<T>(path, { method: 'PATCH', body });
  },
  delete<T>(path: string) {
    return apiRequest<T>(path, { method: 'DELETE' });
  },
  /// Returns the raw `Response` for binary downloads (preview blob, etc.).
  rawPost(path: string, body: unknown, accept = 'image/*') {
    return apiRequest<Response>(path, { method: 'POST', body, accept });
  },
  /// Returns the raw `Response` for thumbnail/resource fetches that need
  /// the bearer header but produce non-JSON.
  rawGet(path: string, accept = 'image/*', query?: ApiRequestOptions['query']) {
    return apiRequest<Response>(path, { method: 'GET', accept, query });
  },
};

/// Fetch a Bearer-authed image and return a `blob:` URL for the
/// `<img src="">` tag. Caller owns revocation via `URL.revokeObjectURL`
/// when the component unmounts.
export async function prefetchAuthedImage(path: string): Promise<string> {
  const resp = await api.rawGet(path);
  const blob = await resp.blob();
  return URL.createObjectURL(retypeForPlayback(blob));
}

/// Chrome and Firefox refuse a blob labelled `video/quicktime` even when
/// the track inside is ordinary H.264 — a `.mov` is ISO-BMFF just like an
/// `.mp4` and their demuxer copes fine, they simply won't try on that MIME
/// type. The server takes the label from the file extension, so every
/// untranscoded iPhone `.mov` arrives tagged that way. Re-label the blob
/// (a zero-copy `slice`) so playback gets a chance. A genuinely HEVC .mov
/// still fails, exactly as it did before — nothing is lost by trying.
function retypeForPlayback(blob: Blob): Blob {
  if (blob.type === 'video/quicktime') {
    return blob.slice(0, blob.size, 'video/mp4');
  }
  return blob;
}

/// Fetch a Bearer-authed file and trigger a browser "Save as" with the
/// given filename. The resource routes require the Authorization header, so
/// a plain `<a href download>` can't reach them — we stream the bytes into a
/// blob (same constraint as image/video display) and save via a synthetic
/// anchor. NOTE: this loads the whole file into memory; fine for photos and
/// family-scale clips, revisit with a token-in-query signed URL if very large
/// videos become common.
export async function downloadAuthedFile(
  path: string,
  filename: string,
): Promise<void> {
  const resp = await api.rawGet(path, '*/*');
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke after a tick so the click has had time to grab the blob.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
